/**
 * useTenant — who the caller is and what they may do, from `GET /me`
 * (Story 22, ADR-0018).
 *
 * Until task 116 this read the role out of the Cognito token
 * (`custom:role`, `cognito:groups`). The API stopped trusting those claims
 * with task 115 and authorizes every route from the caller's
 * `PLATFORM#USERS` record instead; task 117 then deletes the claims. The
 * SPA now asks the API the same question the API asks itself, so the two
 * can never disagree about a control.
 *
 * `status` separates the three ways there is no record to show:
 *   - `loading` — the first `/me` is in flight;
 *   - `no_access` — 403 `no_access`: no record, or a disabled one. Its own
 *     state, NOT an empty role — a security console must say "you have no
 *     access" rather than show a plausible-looking empty screen;
 *   - `error` — anything else, which a retry may clear.
 *
 * Stale time is 60 s to match the server's resolution cache: a role change
 * reaches both sides within the same bound. The acting Admin's own change
 * applies at once on the server, so the Users page invalidates `['me']`.
 *
 * The API is still the only thing that decides. `can()` hides controls the
 * server would refuse; it grants nothing.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import { ApiError, can as canFor, getMe, type Me, type Permission } from '@bluesphere/api-client';

import { useAccessToken } from './useAccessToken';

export type AccessStatus = 'loading' | 'ready' | 'no_access' | 'error';

export interface TenantContext {
  /** The caller's platform record, once `/me` has answered. */
  me: Me | null;
  status: AccessStatus;
  error: unknown;
  refetch: () => void;
  /** Whether the caller holds `permission` on `tenantId`, or anywhere without one. */
  can: (permission: Permission, tenantId?: string) => boolean;
  email: string;
  displayName: string;
}

const isNoAccess = (err: unknown) =>
  err instanceof ApiError && err.status === 403 && err.code === 'no_access';

export function useTenant(): TenantContext {
  const auth = useAuth();
  const idToken = useAccessToken();
  const profile = (auth.user?.profile ?? {}) as Record<string, unknown>;

  const query = useQuery({
    queryKey: ['me'],
    queryFn: () => getMe(idToken),
    enabled: Boolean(idToken),
    staleTime: 60_000,
    // A refusal is an answer, not a fault: retrying it only delays the screen.
    retry: (count, err) => !isNoAccess(err) && count < 1,
  });

  // A refusal wins over a cached record: a user disabled mid-session is
  // shown the no-access screen at the next refetch, not a console that
  // answers 403 to everything. Any other refetch error keeps the record.
  const noAccess = query.isError && isNoAccess(query.error);
  const me = noAccess ? null : (query.data ?? null);
  const status: AccessStatus = noAccess
    ? 'no_access'
    : me
      ? 'ready'
      : query.isError
        ? 'error'
        : 'loading';

  return {
    me,
    status,
    error: query.error,
    refetch: () => void query.refetch(),
    can: (permission, tenantId) => canFor(me, permission, tenantId),
    // The record's values once known; the token's while it loads, so the
    // no-access screen can still say who is signed in.
    email: me?.email ?? (profile.email as string | undefined) ?? '',
    displayName:
      me?.display_name ??
      (profile['custom:display_name'] as string | undefined) ??
      (profile.name as string | undefined) ??
      '',
  };
}
